import {ReactElement, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {DateTime} from "luxon";
import {ChallengeInfoResponse} from "@devour/client";
import {IStore} from "@/redux/defaultStore";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {useGetAsset} from "@/hooks/useAsset";
import AssetImage from "@/components/AssetImage";
import {Button} from "@/components/buttons/Button";
import DevourTopNavUserData from "@/components/DevourTopNavUserData";

// Extended challenge type — navbarConfig is now on ChallengeInfoResponse via generated client
interface ChallengeWithNavbar extends ChallengeInfoResponse {
    start?: number;
    end?: number;
}

interface ChallengeNavbarProps {
    challenge?: ChallengeWithNavbar;
    onLoginRequired: () => void;
    onInviteClick?: () => void;
}

const getCurrentEstTime = () => Date.now();

export const ChallengeNavbar = ({challenge, onLoginRequired, onInviteClick}: ChallengeNavbarProps): ReactElement => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [currentDate, setCurrentDate] = useState(getCurrentEstTime());

    // Get navbar config with defaults
    const navbarConfig = challenge?.navbarConfig;
    const backgroundColor = navbarConfig?.backgroundColor || "#000000";
    const showTimer = navbarConfig?.showTimer !== false; // Default true
    const timerPosition = navbarConfig?.timerPosition || "center";
    const customAvatarImageId = navbarConfig?.customAvatarImageId;

    // Fetch custom avatar asset if configured
    const {data: customAvatarAsset} = useGetAsset(customAvatarImageId || "");
    const customAvatarUrl = customAvatarAsset?.url || customAvatarAsset?.pipeUrl;

    useEffect(() => {
        const interval = setInterval(() => setCurrentDate(getCurrentEstTime()), 1000);
        return () => clearInterval(interval);
    }, []);

    const renderTimeRemaining = () => {
        if (!showTimer || !challenge?.end) return null;
        const now = DateTime.fromMillis(currentDate);
        const future = DateTime.fromMillis(challenge.end);
        const diff = future.diff(now, ["days", "hours", "minutes"]).toObject();

        // Guard against negative values when challenge has ended
        const days = Math.max(0, Math.floor(diff.days || 0));
        const hours = Math.max(0, Math.floor(diff.hours || 0));
        const minutes = Math.max(0, Math.floor(diff.minutes || 0));

        // If challenge has ended, show "Ended" instead of countdown
        if (days <= 0 && hours <= 0 && minutes <= 0) {
            return (
                <span className="challenge-page_navbar_countdown">
                    <span className="challenge-page_navbar_countdown_label">Challenge Ended</span>
                </span>
            );
        }

        return (
            <span className="challenge-page_navbar_countdown">
                <span className="challenge-page_navbar_countdown_label">Challenge Ends:</span>
                <span className="challenge-page_navbar_countdown_time">
                    <strong>{days}</strong>d <strong>{hours}</strong>h <strong>{minutes}</strong>m
                </span>
            </span>
        );
    };

    return (
        <div
            className={`challenge-page_navbar challenge-page_navbar--timer-${timerPosition}`}
            style={{backgroundColor}}
        >
            {challenge?.brandLogo
                ? <AssetImage
                    className="challenge-page_navbar_logo"
                    assetId={challenge.brandLogo as any}
                    alt="Brand Logo"
                />
                : <div />
            }
            {renderTimeRemaining()}
            <div className="challenge-page_navbar_right">
                {fullToken
                    ? <DevourTopNavUserData
                        onShowSignInToast={() => {}}
                        dropdownAnchorElement=".challenge-page_navbar"
                        avatarOnly
                        hideWalletOnMobile={true}
                        customAvatarUrl={customAvatarUrl}
                        hideChallengeSiteMenuItems={true}
                        onInviteClick={onInviteClick}
                    />
                    : <Button variant="ghost"
                        className="challenge-page_navbar_login"
                        onClick={onLoginRequired}


                    >
                        Login
                    </Button>
                }
            </div>
        </div>
    );
};

export default ChallengeNavbar;
